import { Link } from "react-router-dom";
import { Mail, Phone, MapPin, Facebook, Twitter, Instagram, Linkedin, Youtube } from "lucide-react";
import { agencyInfo } from "../mock";

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const quickLinks = [
    { name: "Home", path: "/" },
    { name: "Services", path: "/services" },
    { name: "Portfolio", path: "/portfolio" },
    { name: "About", path: "/about" },
    { name: "Contact", path: "/contact" }
  ];

  const socialLinks = [
    { icon: Facebook, href: "#", label: "Facebook" },
    { icon: Twitter, href: "#", label: "Twitter" },
    { icon: Instagram, href: "#", label: "Instagram" },
    { icon: Linkedin, href: "#", label: "LinkedIn" },
    { icon: Youtube, href: "#", label: "YouTube" }
  ];

  return (
    <footer className="bg-black border-t border-yellow-500/20">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand Section */}
          <div className="space-y-6">
            <div className="flex items-center space-x-3">
              <img
                src={agencyInfo.logo}
                alt="NV Digital"
                className="h-14 w-14 object-contain rounded-full border-2 border-yellow-400/50 p-1"
              />
              <div>
                <h3 className="font-bold text-2xl bg-gradient-to-r from-yellow-400 to-orange-500 bg-clip-text text-transparent">NV Digital</h3>
                <p className="text-sm text-gray-400">Freelance Digital Marketer</p>
              </div>
            </div>
            <p className="text-sm leading-relaxed text-gray-300">
              Your personal freelancer for all digital needs - from design to development
            </p>
            <div className="flex space-x-3">
              {socialLinks.map((social) => {
                const Icon = social.icon;
                return (
                  <a
                    key={social.label}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 bg-gradient-to-br from-gray-800 to-gray-900 border border-yellow-400/30 rounded-lg flex items-center justify-center hover:bg-gradient-to-br hover:from-yellow-400 hover:to-orange-500 hover:border-yellow-400 transition-all duration-300 group"
                    aria-label={social.label}
                  >
                    <Icon size={18} className="text-yellow-400 group-hover:text-black transition-colors" />
                  </a>
                );
              })}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-bold text-lg text-white mb-6 flex items-center">
              <span className="w-1 h-6 bg-gradient-to-b from-yellow-400 to-orange-500 mr-2 rounded"></span>
              Quick Links
            </h4>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-sm text-gray-300 hover:text-yellow-400 transition-colors duration-300 flex items-center group"
                  >
                    <span className="w-1 h-1 bg-yellow-400 rounded-full mr-3 group-hover:w-2 group-hover:h-2 transition-all"></span>
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="font-bold text-lg text-white mb-6 flex items-center">
              <span className="w-1 h-6 bg-gradient-to-b from-orange-500 to-yellow-400 mr-2 rounded"></span>
              My Services
            </h4>
            <ul className="space-y-3">
              <li className="text-sm text-gray-300 flex items-center group">
                <span className="w-1 h-1 bg-orange-500 rounded-full mr-3 group-hover:w-2 group-hover:h-2 transition-all"></span>
                Video Editing
              </li>
              <li className="text-sm text-gray-300 flex items-center group">
                <span className="w-1 h-1 bg-orange-500 rounded-full mr-3 group-hover:w-2 group-hover:h-2 transition-all"></span>
                Content Creation
              </li>
              <li className="text-sm text-gray-300 flex items-center group">
                <span className="w-1 h-1 bg-orange-500 rounded-full mr-3 group-hover:w-2 group-hover:h-2 transition-all"></span>
                Full Stack Development
              </li>
              <li className="text-sm text-gray-300 flex items-center group">
                <span className="w-1 h-1 bg-orange-500 rounded-full mr-3 group-hover:w-2 group-hover:h-2 transition-all"></span>
                Digital Marketing
              </li>
              <li className="text-sm text-gray-300 flex items-center group">
                <span className="w-1 h-1 bg-orange-500 rounded-full mr-3 group-hover:w-2 group-hover:h-2 transition-all"></span>
                Graphic Design
              </li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="font-bold text-lg text-white mb-6 flex items-center">
              <span className="w-1 h-6 bg-gradient-to-b from-blue-400 to-blue-600 mr-2 rounded"></span>
              Contact Me
            </h4>
            <ul className="space-y-4">
              <li className="flex items-start space-x-3">
                <Phone size={18} className="text-yellow-400 mt-1 flex-shrink-0" />
                <div className="text-sm">
                  <a href={`https://wa.me/${agencyInfo.whatsapp[0].replace(/\+/g, '')}`} className="text-gray-300 hover:text-yellow-400 transition-colors duration-300 block">
                    {agencyInfo.whatsapp[0]}
                  </a>
                  <a href={`https://wa.me/${agencyInfo.whatsapp[1].replace(/\+/g, '')}`} className="text-gray-300 hover:text-yellow-400 transition-colors duration-300 block">
                    {agencyInfo.whatsapp[1]}
                  </a>
                </div>
              </li>
              <li className="flex items-start space-x-3">
                <Mail size={18} className="text-yellow-400 mt-1 flex-shrink-0" />
                <a href={`mailto:${agencyInfo.email}`} className="text-sm text-gray-300 hover:text-yellow-400 transition-colors duration-300">
                  {agencyInfo.email}
                </a>
              </li>
              <li className="flex items-start space-x-3">
                <MapPin size={18} className="text-yellow-400 mt-1 flex-shrink-0" />
                <div className="text-sm space-y-1 text-gray-300">
                  {agencyInfo.locations.map((location, index) => (
                    <div key={index} className="flex items-center space-x-2">
                      <span>{location.flag}</span>
                      <span>{location.full}</span>
                    </div>
                  ))}
                </div>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-yellow-500/20 mt-12 pt-8 flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
          <p className="text-sm text-gray-400">
            © {currentYear} {agencyInfo.name}. All rights reserved.
          </p>
          <div className="flex items-center space-x-2 text-sm">
            <span className="text-gray-400">Created & Managed by</span>
            <Link to="/" className="font-bold bg-gradient-to-r from-yellow-400 to-orange-500 bg-clip-text text-transparent hover:from-orange-500 hover:to-yellow-400 transition-all duration-300">
              Karthipan Karunamoorthy
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;